import type { CSSProperties, DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { Album, AlbumCatalogue, AlbumMap, AlbumPage, AlbumSlotFrame, AlbumStyles, AlbumView, AlbumPicture, TripSummary } from '../../types';
import { albumFonts, albumLook, maxCharsAt, slotFont, slotPt } from '../../utils/albumLook';
import { fontCss } from '../../utils/albumFonts';
import AlbumFramedPhoto from './AlbumFramedPhoto';
import AlbumPageBackground from './AlbumPageBackground';
import AlbumInlineText from './AlbumInlineText';
import { tripPhotoUrl } from '../../utils/tripPhotos';
import { Icon, Image, Type, AlignLeft, Map as MapIcon, X } from '../ui/Icon';
import { albumMapKey, isTripMap } from '../../utils/albumMaps';
import type { PhotoPosition } from '../../hooks/usePhotoPan';
import AlbumMapView from './AlbumMapView';
import AlbumStatsPage from './AlbumStatsPage';

export const PHOTO_DRAG_TYPE = 'application/x-gromly-photo';

const MM_PER_PT = 0.3528;

const ON_PHOTO: CSSProperties = {
  color: '#fdfaf3',
  background: 'linear-gradient(to top, rgba(15, 23, 42, 0.6), rgba(15, 23, 42, 0.2))',
  textShadow: '0 1px 2px rgba(15, 23, 42, 0.5)',
};

interface Props {
  page: AlbumPage;
  catalogue: AlbumCatalogue;
  photosById: Map<string, AlbumPicture>;
  summary: TripSummary | null;
  maps: Record<string, AlbumMap>;
  trip: AlbumView['trip'];
  styles: AlbumStyles;
  /** The album's theme, typeface and photo frame ids (ADR-0048). */
  albumStyle: Pick<Album, 'theme' | 'fontPair' | 'photoFrame'>;
  /** ADR-0052: the canvas width in mm — two pages for a panorama, plus the spine for the cover's wrap. */
  width?: number;
  /** Inside a spread: the spread draws the edge, not each page. */
  bare?: boolean;
  /** Thumbnails: no placeholders, no interaction. */
  compact?: boolean;
  selectedIndex?: number | null;
  onSlotClick?: (index: number) => void;
  onPhotoDrop?: (index: number, photoId: string) => void;
  /** Empties the selected photo slot, from a button on the photo itself or Delete/Backspace. */
  onPhotoRemove?: (index: number) => void;
  /** Dragging the selected photo inside its slot. */
  onPhotoMove?: (index: number, position: PhotoPosition) => void;
  /** Typing straight into the selected title or text slot. */
  onTextCommit?: (index: number, text: string | null) => void;
}

const SLOT_ICON = { PHOTO: Image, TITLE: Type, TEXT: AlignLeft, MAP: MapIcon } as const;

/**
 * ADR-0046: one page as it will print, on the paper, type and frames the
 * album's style gives it (ADR-0048).
 * The frames are the server's, in mm; here they only become percentages.
 */
export default function AlbumPageView({
  page, catalogue, photosById, summary, maps, trip, styles, albumStyle, width, bare = false, compact = false, selectedIndex = null, onSlotClick, onPhotoDrop, onPhotoRemove, onPhotoMove, onTextCommit,
}: Props) {
  const { t } = useTranslation();
  const W = width ?? catalogue.page.width;
  const H = catalogue.page.height;
  // An unknown template (a newer server) draws as an empty page, never an error.
  const frames = catalogue.templates.find(tp => tp.id === page.template)?.slots ?? [];
  const fontSize = (pt: number) => `${(pt * MM_PER_PT / W) * 100}cqw`;
  const look = albumLook(styles, albumStyle.theme, page);
  const fonts = albumFonts(styles, albumStyle.fontPair);

  function box(f: AlbumSlotFrame): CSSProperties {
    return { left: `${(f.x / W) * 100}%`, top: `${(f.y / H) * 100}%`, width: `${(f.w / W) * 100}%`, height: `${(f.h / H) * 100}%` };
  }

  function handleDrop(e: DragEvent, index: number) {
    const photoId = e.dataTransfer.getData(PHOTO_DRAG_TYPE);
    if (!photoId || !onPhotoDrop) return;
    e.preventDefault();
    onPhotoDrop(index, photoId);
  }

  return (
    <div
      className={`relative w-full overflow-hidden select-none ${bare ? '' : 'shadow-sm ring-1 ring-slate-200 dark:ring-slate-700'}`}
      style={{ aspectRatio: `${W} / ${H}`, containerType: 'inline-size', color: look.ink.text }}
    >
      <AlbumPageBackground look={look} photo={look.photo ? photosById.get(look.photo.id) : undefined} width={W} height={H} />
      {page.template === 'STATS' && (
        <AlbumStatsPage
          summary={summary}
          trip={trip}
          style={look.map}
          ink={look.ink}
          fonts={fonts}
          frame={catalogue.figuresMap}
          scale={catalogue.figuresScale}
          margin={catalogue.margin}
          figuresMap={page.figuresMap}
          figuresRoute={page.figuresRoute}
          figuresOutline={page.figuresOutline}
          countryMap={maps[albumMapKey('STATS', null)]}
          pageWidth={W}
        />
      )}
      {frames.map((frame, index) => {
        const slot = page.slots.find(s => s.index === index);
        const photoId = slot?.photoId ?? slot?.albumPhotoId;
        const photo = photoId ? photosById.get(photoId) : undefined;
        const text = slot?.text ?? '';
        // A trip map draws itself; a day map needs its day (ADR-0047).
        const map = frame.kind === 'MAP' && (isTripMap(page.template) || slot?.dayId)
          ? maps[albumMapKey(page.template, slot?.dayId ?? null, page.mapLegend !== false)]
          : undefined;
        const filled = frame.kind === 'PHOTO' ? !!photo : frame.kind === 'MAP' ? !!map : !!text;
        // ADR-0049: a smaller format holds less; the text stays, marked, for the traveller to shorten.
        const pt = frame.kind === 'TITLE' || frame.kind === 'TEXT' ? slotPt(styles, catalogue.fontPt, frame, slot) : 0;
        const limit = frame.maxChars ? maxCharsAt(catalogue.fontPt, frame, pt) : 0;
        const overflowing = !compact && !!limit && text.length > limit;
        const textFont = slotFont(styles, fonts, frame.kind === 'TITLE' ? 'TITLE' : 'TEXT', slot?.font ?? null);
        const textSize = fontSize(pt);
        const textAlign = slot?.textAlign ?? frame.align ?? 'left';
        const selected = selectedIndex === index;
        const editing = selected && !!onTextCommit && (frame.kind === 'TITLE' || frame.kind === 'TEXT');
        const interactive = !compact && !!onSlotClick;
        const removable = selected && !compact && frame.kind === 'PHOTO' && !!photo && !!onPhotoRemove;
        return (
          <div
            key={index}
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : undefined}
            aria-label={interactive ? t(`album.slot.${frame.kind}`) : undefined}
            aria-pressed={interactive ? selected : undefined}
            onClick={interactive ? () => onSlotClick(index) : undefined}
            onKeyDown={interactive ? e => {
              if (e.target !== e.currentTarget) return;
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSlotClick(index); }
              else if (removable && (e.key === 'Delete' || e.key === 'Backspace')) { e.preventDefault(); onPhotoRemove(index); }
            } : undefined}
            onDragOver={frame.kind === 'PHOTO' && onPhotoDrop ? e => e.preventDefault() : undefined}
            onDrop={frame.kind === 'PHOTO' && onPhotoDrop ? e => handleDrop(e, index) : undefined}
            className={`absolute overflow-hidden ${interactive && !editing ? 'cursor-pointer' : ''} ${
              selected ? 'outline outline-2 outline-teal-500 outline-offset-1'
                : overflowing ? 'outline outline-2 outline-dashed outline-red-500'
                  : interactive ? 'hover:outline hover:outline-1 hover:outline-teal-300' : ''
            }`}
            title={overflowing ? t('album.tooLong') : undefined}
            style={frame.onPhoto ? { ...box(frame), ...ON_PHOTO } : box(frame)}
          >
            {frame.kind === 'PHOTO' && photo && (
              <AlbumFramedPhoto
                // A new saved position replaces the one left over from the drag.
                key={`${slot?.photoX}:${slot?.photoY}`}
                src={tripPhotoUrl(photo.thumbnailFilename ?? photo.filename)}
                frame={albumStyle.photoFrame}
                pageWidth={W}
                position={slot?.photoX != null && slot.photoY != null ? { x: slot.photoX, y: slot.photoY } : null}
                onMove={removable && onPhotoMove ? position => onPhotoMove(index, position) : undefined}
              />
            )}
            {removable && (
              <button
                type="button"
                onClick={e => { e.stopPropagation(); onPhotoRemove(index); }}
                title={t('album.removePhoto')}
                aria-label={t('album.removePhoto')}
                className="absolute top-1.5 right-1.5 w-7 h-7 flex items-center justify-center rounded-full bg-black/60 text-white hover:bg-red-500 cursor-pointer"
              >
                <Icon as={X} size={14} />
              </button>
            )}
            {map && <AlbumMapView map={map} style={look.map} />}
            {editing && (
              <AlbumInlineText
                key={`${page.id}-${index}-${text}`}
                kind={frame.kind as 'TITLE' | 'TEXT'}
                initial={text}
                maxChars={limit}
                fontSize={textSize}
                font={textFont}
                align={textAlign}
                muted={look.ink.muted}
                onCommit={next => onTextCommit!(index, next)}
              />
            )}
            {!editing && (frame.kind === 'TITLE' || frame.kind === 'TEXT') && text && (
              <p
                className={`w-full h-full whitespace-pre-wrap break-words ${frame.kind === 'TITLE' ? 'leading-tight' : 'leading-snug'}`}
                style={{ fontSize: textSize, textAlign, ...fontCss(textFont) }}
              >
                {text}
              </p>
            )}
            {!filled && !compact && !editing && (
              <div className="w-full h-full flex flex-col items-center justify-center gap-1 border border-dashed"
                style={{ borderColor: frame.onPhoto ? 'currentColor' : look.ink.muted, color: frame.onPhoto ? 'inherit' : look.ink.muted, background: 'rgba(127, 127, 127, 0.08)' }}>
                <Icon as={SLOT_ICON[frame.kind]} size={16} />
                <span className="text-[10px] font-medium">{t(`album.slot.${frame.kind}`)}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
